import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Popconfirm, Radio, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SnippetsOutlined,
} from '@ant-design/icons';
import { useBrewsQuery, useDeleteBrew, useUpdateBrew } from '../hooks';
import * as fmt from '../format';
import PasteBrewsModal from './PasteBrewsModal';

const { Text, Paragraph } = Typography;

const ACTIVE = ['planned', 'brewing'];

const ScheduleTab = ({ plan, canEdit, onOpenBrewModal }) => {
  const [filter, setFilter] = useState('active');
  const [pasteOpen, setPasteOpen] = useState(false);
  const brewsQuery = useBrewsQuery();
  const deleteBrew = useDeleteBrew();
  const updateBrew = useUpdateBrew();

  // How the planner used each brew: counted as incoming stock, or matched to a Cin7 tank gyle.
  const planInfo = useMemo(() => {
    const byId = {};
    plan.beers.forEach((b) => b.plannedBrews.forEach((pb) => { byId[pb.id] = pb; }));
    return byId;
  }, [plan]);

  const brews = (brewsQuery.data || []).filter((b) => filter === 'all' || ACTIVE.includes(b.status));

  const columns = [
    { title: 'Brew date', dataIndex: 'brewDate', render: fmt.date },
    { title: 'Beer', dataIndex: 'beerName', render: (name) => <Text strong>{name}</Text> },
    { title: 'Brewery', dataIndex: 'brewery' },
    {
      title: 'Volume', dataIndex: 'volumeLitres', align: 'right', render: fmt.litres,
    },
    {
      title: 'Ready',
      render: (_, b) => {
        const info = planInfo[b.id];
        if (b.packageDate) return fmt.shortDate(b.packageDate);
        return info?.eta ? <Tooltip title="Brew date + lead time">{`~${fmt.shortDate(info.eta)}`}</Tooltip> : '—';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status, b) => {
        const info = planInfo[b.id];
        return (
          <Space size={4} wrap>
            <Tag color={fmt.BREW_STATUS[status]?.color}>{fmt.BREW_STATUS[status]?.label}</Tag>
            {info && !info.counted && (
              <Tooltip title={info.linkReason === 'auto'
                ? 'Matched to a Cin7 tank gyle brewed the same week'
                : 'Matched by gyle code'}
              >
                <Tag>{`in tank ${info.linkedGyle}`}</Tag>
              </Tooltip>
            )}
            {info?.overdue && <Tag color="red">no gyle yet</Tag>}
          </Space>
        );
      },
    },
    { title: 'Gyle', dataIndex: 'gyleCode', render: (g) => g || '—' },
    {
      title: 'Pack split',
      dataIndex: 'packSplit',
      render: (split) => (split?.length
        ? <Text type="secondary">{split.map((p) => `${fmt.number(p.units)} × ${p.sku}`).join(', ')}</Text>
        : <Text type="secondary">auto</Text>),
    },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
    {
      title: '',
      width: 170,
      render: (_, b) => (canEdit ? (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => onOpenBrewModal(b)} />
          </Tooltip>
          {ACTIVE.includes(b.status) && (
            <Tooltip title="Mark packaged (stock is now in Cin7)">
              <Button
                size="small"
                icon={<CheckOutlined />}
                onClick={() => updateBrew.mutate({ id: b.id, status: 'packaged' })}
              />
            </Tooltip>
          )}
          <Popconfirm title="Remove this brew?" onConfirm={() => deleteBrew.mutate(b.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) : null),
    },
  ];

  return (
    <div>
      <Paragraph type="secondary" style={{ maxWidth: 820 }}>
        Planned brews count as incoming stock from their ready date. Once a brew is in a tank,
        Cin7 tank beer takes over: add the gyle code (or it is matched automatically when brewed
        within a week of the planned date) so it isn&apos;t counted twice. Mark brews packaged
        once they are in stock.
      </Paragraph>
      <div className="forecasting-toolbar">
        <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} buttonStyle="solid">
          <Radio.Button value="active">Planned &amp; brewing</Radio.Button>
          <Radio.Button value="all">All</Radio.Button>
        </Radio.Group>
        {canEdit && (
          <Space>
            <Button icon={<SnippetsOutlined />} onClick={() => setPasteOpen(true)}>
              Paste from spreadsheet
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenBrewModal(null)}>
              Add brew
            </Button>
          </Space>
        )}
      </div>
      <Table
        rowKey="id"
        size="middle"
        loading={brewsQuery.isLoading}
        columns={columns}
        dataSource={brews}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        locale={{ emptyText: 'No brews scheduled yet' }}
      />
      <PasteBrewsModal open={pasteOpen} plan={plan} onClose={() => setPasteOpen(false)} />
    </div>
  );
};

ScheduleTab.propTypes = {
  plan: PropTypes.shape({ beers: PropTypes.arrayOf(PropTypes.object) }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onOpenBrewModal: PropTypes.func.isRequired,
};

export default ScheduleTab;
